import { Alert, Button, CircularProgress, InputAdornment, Skeleton, Typography } from '@mui/material';
import { Add, CalendarToday, Search, Today } from '@mui/icons-material';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useState } from 'react';

import Appbar from 'components/appbar/Appbar';
import { moneyFormat } from 'helpers/moneyFormat';
import history from 'services/history';
import { Guide } from 'types/guide';
import { getGuidePosition, getGuideDate, getGuideTime, getSlotLayout, getTone } from './agenda';
import GuideDetailsDialog from './GuideDetailsDialog';
import {
  AgendaCard,
  AgendaTopbar,
  AppointmentBlock,
  AppointmentMeta,
  BrandArea,
  BrandBadge,
  CalendarBody,
  CalendarBoard,
  CalendarCorner,
  CalendarHeader,
  DayColumn,
  DayHeader,
  DaysGrid,
  EmptyOverlay,
  HomeShell,
  HourLine,
  LoadingColumn,
  LoadingState,
  SearchInput,
  SummaryItem,
  SummaryStrip,
  TimeColumn,
  TimeMark,
  TopbarActions,
  WeekPill,
} from './HomeStyles';
import { useWeekAgenda } from './hooks/useWeekAgenda';

const Home: React.FC = () => {
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { isLoading, hasError, weekDays, hours, filteredGuides, nextGuide, totalValue, guideCountLabel } =
    useWeekAgenda(searchTerm);

  return (
    <div>
      <Appbar title="Início" />

      <HomeShell>
        <AgendaCard>
          <AgendaTopbar>
            <BrandArea>
              <BrandBadge>
                <CalendarToday />
              </BrandBadge>
              <div>
                <Typography variant="overline">Agenda semanal</Typography>
                <Typography variant="h5">{format(new Date(), 'MMMM, yyyy', { locale: ptBR })}</Typography>
              </div>
            </BrandArea>

            <SearchInput
              placeholder="Buscar consulta"
              size="small"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              }
            />

            <TopbarActions>
              <WeekPill>Semana</WeekPill>
              <Button onClick={() => history.push('/guides/new')} size="small" startIcon={<Add />} variant="contained">
                Nova consulta
              </Button>
            </TopbarActions>
          </AgendaTopbar>

          <SummaryStrip>
            <SummaryItem>
              <Typography variant="caption">Hoje</Typography>
              <Typography variant="h6">{guideCountLabel}</Typography>
            </SummaryItem>
            <SummaryItem>
              <Typography variant="caption">Próximo horário</Typography>
              <Typography variant="h6">{nextGuide ? getGuideTime(nextGuide.appointment_date) : '--:--'}</Typography>
            </SummaryItem>
            <SummaryItem>
              <Typography variant="caption">Valor previsto hoje</Typography>
              <Typography variant="h6">{moneyFormat(totalValue)}</Typography>
            </SummaryItem>
          </SummaryStrip>

          {hasError && <Alert severity="error">Não foi possível carregar a agenda da semana.</Alert>}

          <CalendarBoard>
            <CalendarHeader>
              <CalendarCorner>
                <Today fontSize="small" />
              </CalendarCorner>
              {weekDays.map(day => (
                <DayHeader key={day.toISOString()} istoday={isSameDay(day, new Date()) ? 1 : 0}>
                  <Typography variant="caption">{format(day, 'EEEE', { locale: ptBR })}</Typography>
                  <Typography variant="body2">{format(day, 'dd/MM')}</Typography>
                </DayHeader>
              ))}
            </CalendarHeader>

            <CalendarBody>
              <TimeColumn>
                {hours.map(hour => (
                  <TimeMark key={hour}>{`${String(hour).padStart(2, '0')}:00`}</TimeMark>
                ))}
              </TimeColumn>

              <DaysGrid>
                {weekDays.map(day => (
                  <DayColumn key={day.toISOString()}>
                    {hours.slice(0, -1).map(hour => (
                      <HourLine key={hour} />
                    ))}

                    {isLoading && (
                      <LoadingColumn>
                        <Skeleton variant="rounded" height={78} />
                        <Skeleton variant="rounded" height={78} />
                      </LoadingColumn>
                    )}

                    {!isLoading &&
                      (() => {
                        const dayGuides = filteredGuides.filter(guide => isSameDay(getGuideDate(guide), day));

                        return dayGuides.map(guide => {
                          const position = getGuidePosition(guide);
                          const isSelected = selectedGuide?.id === guide.id;
                          const { slotCount, slotIndex } = getSlotLayout(guide, dayGuides);
                          const itemGap = 3;
                          const slotHeight = Math.max(18, (position.height - (slotCount - 1) * itemGap) / slotCount);

                          return (
                            <AppointmentBlock
                              key={guide.id}
                              isselected={isSelected ? 1 : 0}
                              style={{
                                top: position.top + slotIndex * (slotHeight + itemGap),
                                height: slotHeight,
                              }}
                              tone={getTone(guide)}
                              type="button"
                              onClick={() => setSelectedGuide(guide)}
                            >
                              <Typography variant="body2">{guide.patient?.name || 'Paciente não informado'}</Typography>
                              <AppointmentMeta>{getGuideTime(guide.appointment_date)}</AppointmentMeta>
                            </AppointmentBlock>
                          );
                        });
                      })()}
                  </DayColumn>
                ))}

                {!isLoading && filteredGuides.length === 0 && (
                  <EmptyOverlay>
                    <Typography variant="h6">Nenhuma consulta encontrada</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ajuste a busca ou cadastre uma nova consulta.
                    </Typography>
                  </EmptyOverlay>
                )}
              </DaysGrid>

              {isLoading && (
                <LoadingState>
                  <CircularProgress size={24} />
                  <Typography variant="body2">Carregando agenda...</Typography>
                </LoadingState>
              )}
            </CalendarBody>
          </CalendarBoard>

          <GuideDetailsDialog guide={selectedGuide} onClose={() => setSelectedGuide(null)} />
        </AgendaCard>
      </HomeShell>
    </div>
  );
};

export default Home;
